import { clsx } from "clsx";
import { forwardRef, InputHTMLAttributes, MouseEvent, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import {
  getWrapperClassNames,
  inputClassNames,
  MaxInputHeight,
  maxInputHeightClassNames,
} from "./class-names";
import { Icon } from "../icon";
import { IconName } from "../icon/available-icons";

export type BaseInputProps = {
  label?: string;
  mandatory?: boolean;
  leftIcon?: IconName;
  message?: string;
  error?: boolean;
  className?: string;
  rightButtonIcon?: IconName;
  dataTestId?: string;
  onRightButtonClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  contentOverride?: ReactNode;
  inputElement?: ReactNode;
  compact?: boolean;
  maxHeight?: MaxInputHeight;
  highlighted?: boolean;
} & Pick<
  InputHTMLAttributes<HTMLInputElement>,
  | "id"
  | "disabled"
  | "name"
  | "title"
  | "type"
  | "placeholder"
  | "value"
  | "defaultValue"
  | "onChange"
  | "onBlur"
  | "onFocus"
  | "onKeyDown"
  | "readOnly"
  | "inputMode"
  | "formNoValidate"
  | "minLength"
  | "maxLength"
  | "pattern"
  | "autoComplete"
  | "autoFocus"
  | "onClick"
  | "aria-errormessage"
>;

export const BaseInput = forwardRef<HTMLInputElement, BaseInputProps>(
  (
    {
      id,
      leftIcon,
      rightButtonIcon,
      error,
      message,
      disabled,
      dataTestId,
      className,
      onRightButtonClick,
      contentOverride,
      inputElement,
      compact,
      maxHeight = "full",
      highlighted,
      ...inputProps
    }: BaseInputProps,
    ref,
  ) => (
    <div
      className={twMerge(
        clsx(
          getWrapperClassNames({ error, disabled, compact, highlighted }),
          className,
        ),
      )}
      aria-disabled={disabled}
      data-testid={dataTestId}
      data-highlighted={highlighted || undefined}
    >
      {leftIcon && (
        <Icon className="w-[1.5rem] h-[1.5rem] px-2" name={leftIcon} />
      )}
      <div className={clsx("flex flex-1", maxInputHeightClassNames[maxHeight])}>
        {contentOverride ? (
          <div className="min-h-[2.5rem] w-full">{contentOverride}</div>
        ) : (
          inputElement || (
            <input
              id={id}
              disabled={disabled}
              ref={ref}
              aria-invalid={error}
              aria-errormessage={error ? message : undefined}
              className={clsx(inputClassNames, "flex-1")}
              data-testid={`${dataTestId || "text-field"}-input`}
              {...inputProps}
            />
          )
        )}
      </div>
      {rightButtonIcon && (
        <button
          className="flex items-center gap-1 p-1 focus-visible:outline-none disabled:cursor-not-allowed"
          type="button"
          disabled={disabled}
          onClick={onRightButtonClick}
          data-testid={`${dataTestId || "base-input"}-button`}
        >
          <Icon className="w-[1.5rem] h-[1.5rem]" name={rightButtonIcon} />
        </button>
      )}
    </div>
  ),
);
